import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import {
  inviteMember,
  listMembers,
  type MemberStatus,
  removeMember,
  type TenantMember,
} from "../../../members/api";

interface Roster {
  members: TenantMember[];
  canManage: boolean;
}

/**
 * A roster write (invite or remove). Both answer with the fresh roster, so the
 * response seeds the query rather than costing a refetch — which is only sound
 * while responses can't arrive out of order, so every write shares one mutation
 * scope and they run one at a time. Removing two members at once would
 * otherwise let the older roster land last and restore one of them.
 */
function useRosterWrite(
  write: (email: string) => Promise<{ members: TenantMember[] }>,
  onDone?: () => void,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: write,
    scope: { id: "members" },
    onSuccess: ({ members }) => {
      qc.setQueryData(["members"], (prev: Roster | undefined) =>
        prev ? { ...prev, members } : { members, canManage: true },
      );
      onDone?.();
    },
  });
}

export const Route = createFileRoute("/_workspace/settings/members")({
  component: MembersPane,
});

function MembersPane() {
  const roster = useQuery({
    queryKey: ["members"],
    queryFn: listMembers,
  });

  return (
    <div className="mx-auto w-full max-w-2xl px-6 pt-8 pb-12">
      <h1 className="text-lg font-semibold">Members</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Everyone in this space. Invite someone by email and they'll join the
        moment they sign in with that address.
      </p>

      <section className="mt-6">
        {roster.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : roster.error ? (
          <p className="text-sm text-destructive">
            {(roster.error as Error).message}
          </p>
        ) : roster.data ? (
          <>
            <MemberTable
              members={roster.data.members}
              canManage={roster.data.canManage}
            />
            {roster.data.canManage ? (
              <InviteForm />
            ) : (
              <p className="mt-4 text-sm text-muted-foreground">
                Only the workspace owner can invite new members.
              </p>
            )}
          </>
        ) : null}
      </section>
    </div>
  );
}

/** The roster as a table: one row per member, with role and standing. */
function MemberTable({
  members,
  canManage,
}: {
  members: TenantMember[];
  canManage: boolean;
}) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Member</TableHead>
          <TableHead>Role</TableHead>
          <TableHead className="text-right">Status</TableHead>
          {canManage ? (
            <TableHead className="text-right">
              <span className="sr-only">Actions</span>
            </TableHead>
          ) : null}
        </TableRow>
      </TableHeader>
      <TableBody>
        {members.map((m) => (
          <TableRow key={m.email}>
            <TableCell>
              <div className="min-w-0">
                <div className="truncate font-medium">{m.name ?? m.email}</div>
                {m.name ? (
                  <div className="truncate text-xs text-muted-foreground">
                    {m.email}
                  </div>
                ) : null}
              </div>
            </TableCell>
            <TableCell className="capitalize text-muted-foreground">
              {m.role}
            </TableCell>
            <TableCell className="text-right">
              <StatusBadge status={m.status} />
            </TableCell>
            {canManage ? (
              <TableCell className="text-right">
                {/* The owner can't be removed, so they get no button at all. */}
                {m.role === "owner" ? null : <RemoveButton member={m} />}
              </TableCell>
            ) : null}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/** Per-row remove, shown to the owner. Destructive, so it asks first. */
function RemoveButton({ member }: { member: TenantMember }) {
  const remove = useRosterWrite(removeMember);
  const who = member.name ?? member.email;
  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        disabled={remove.isPending}
        onClick={() => {
          if (
            window.confirm(`Remove ${who} from this space? They lose access.`)
          )
            remove.mutate(member.email);
        }}
      >
        {remove.isPending ? "Removing…" : "Remove"}
      </Button>
      {remove.error ? (
        <p className="mt-1 text-xs text-destructive">
          {(remove.error as Error).message}
        </p>
      ) : null}
    </>
  );
}

/** Standing badge: solid when the member has joined, muted while pending. */
function StatusBadge({ status }: { status: MemberStatus }) {
  return status === "active" ? (
    <Badge variant="secondary">Active</Badge>
  ) : (
    <Badge variant="outline">Pending</Badge>
  );
}

/** Invite-by-email form, shown to the owner only. */
function InviteForm() {
  const [value, setValue] = useState("");
  const invite = useRosterWrite(inviteMember, () => setValue(""));

  return (
    <form
      className="mt-6"
      onSubmit={(e) => {
        e.preventDefault();
        const email = value.trim();
        if (email) invite.mutate(email);
      }}
    >
      <h2 className="text-sm font-semibold">Invite a member</h2>
      <div className="mt-3 flex max-w-sm items-center gap-2">
        <Input
          type="email"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="name@example.com"
          aria-label="Invite by email"
          disabled={invite.isPending}
        />
        <Button type="submit" disabled={!value.trim() || invite.isPending}>
          {invite.isPending ? "Inviting…" : "Invite"}
        </Button>
      </div>
      {invite.error ? (
        <p className="mt-2 text-sm text-destructive">
          {(invite.error as Error).message}
        </p>
      ) : null}
    </form>
  );
}
